import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import SpinnerButton from "@/components/SpinnerButton";
import { adminApi } from "@/lib/api";

const FIELDS = [
  { key: "logo_url", label: "Logo URL" },
  { key: "daily_summary_time", label: "Daily summary time (HH:MM, MYT)" },
  { key: "daily_summary_recipient", label: "Daily summary recipient" },
  { key: "walkin_pin", label: "Walk-in PIN (4 digits)" },
  { key: "customer_thank_you_message", label: "Customer thank-you message", textarea: true },
  { key: "store_name_kl", label: "Store name (KL)" },
  { key: "store_address_kl", label: "Store address (KL)", textarea: true },
  { key: "store_phone_kl", label: "Store phone (KL)" },
  { key: "store_maps_url_kl", label: "Google Maps link (KL)" },
  { key: "review_url_kl", label: "Google review link (KL)" },
  // Pass 43 — JB store profile. Mirrors the KL block.
  { key: "store_name_jb", label: "Store name (JB)" },
  { key: "store_address_jb", label: "Store address (JB)", textarea: true },
  { key: "store_phone_jb", label: "Store phone (JB)" },
  { key: "store_maps_url_jb", label: "Google Maps link (JB)" },
  { key: "review_url_jb", label: "Google review link (JB)" },
  { key: "turnstile_site_key", label: "Cloudflare Turnstile site key" },
  { key: "gtm_container_id", label: "Google Tag Manager container ID (GTM-XXXXXX)" },
  { key: "meta_pixel_id", label: "Meta Pixel ID" },
  { key: "smtp_host", label: "SMTP host" },
  { key: "smtp_port", label: "SMTP port" },
  { key: "smtp_user", label: "SMTP username" },
  { key: "smtp_pass", label: "SMTP password" },
];

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const [cur, setCur] = useState("");
  const [np, setNp] = useState("");
  const [pwMsg, setPwMsg] = useState("");
  const [pwErr, setPwErr] = useState("");
  const [pwLoading, setPwLoading] = useState(false);

  useEffect(() => {
    adminApi.settings().then((r) => setSettings(r.data));
  }, []);

  const save = async () => {
    setMsg(""); setErr(""); setLoading(true);
    try {
      const res = await adminApi.settingsPatch(settings);
      setSettings(res.data);
      setMsg("Settings saved.");
    } catch (e) { setErr(e?.response?.data?.detail || "Failed."); }
    finally { setLoading(false); }
  };

  const changePassword = async () => {
    setPwMsg(""); setPwErr(""); setPwLoading(true);
    try {
      await adminApi.passwordChange({ current_password: cur, new_password: np });
      setPwMsg("Password updated.");
      setCur(""); setNp("");
    } catch (e) { setPwErr(e?.response?.data?.detail || "Failed."); }
    finally { setPwLoading(false); }
  };

  if (!settings) return <AdminLayout>{"Loading\u2026"}</AdminLayout>;

  return (
    <AdminLayout>
      <p className="scw-eyebrow" style={{ marginBottom: 8 }}>Configuration</p>
      <h1 style={{ fontSize: 28, margin: "0 0 28px 0" }}>Settings</h1>

      <div className="scw-card" style={{ marginBottom: 24 }} data-testid="settings-card">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
          {FIELDS.map((f) => (
            <div key={f.key} style={{ gridColumn: f.textarea ? "1 / -1" : "auto" }}>
              <label className="scw-label">{f.label}</label>
              {f.textarea ? (
                <textarea className="scw-textarea" value={settings[f.key] || ""} onChange={(e) => setSettings({ ...settings, [f.key]: e.target.value })} data-testid={`settings-${f.key}`} />
              ) : (
                <input className="scw-input" value={settings[f.key] || ""} onChange={(e) => setSettings({ ...settings, [f.key]: e.target.value })} data-testid={`settings-${f.key}`} />
              )}
            </div>
          ))}
        </div>
        <p style={{ marginTop: 12, fontSize: 12, color: "var(--scw-muted)" }}>
          The store name/address/phone fields above appear in customer
          confirmation emails. Empty values are tolerated, but a JB
          lead with no JB address won&apos;t include an address in its
          confirmation email.
        </p>
        <div style={{ borderTop: "1px solid var(--scw-line)", marginTop: 20, paddingTop: 20 }}>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={(settings.auto_review_email || "").toLowerCase() === "true"}
              onChange={(e) => setSettings({ ...settings, auto_review_email: e.target.checked ? "true" : "false" })}
              style={{ marginTop: 3 }}
              data-testid="settings-auto-review-email"
            />
            <span>
              <span style={{ display: "block", fontSize: 14 }}>Auto-send review request emails</span>
              <span style={{ display: "block", fontSize: 12, color: "var(--scw-muted)", marginTop: 4, lineHeight: 1.6 }}>
                Once a day, any closed-won lead that has entered its review
                window (3–14 days after the win) and has a customer email
                on file gets the request sent automatically, signed by the
                assigned specialist — no rep click needed. WhatsApp always
                stays manual on the Reviews tab. Off by default.
              </span>
            </span>
          </label>
        </div>
        {msg && <p style={{ color: "#3a7d3a", fontSize: 13, marginTop: 16 }} data-testid="settings-msg">{msg}</p>}
        {err && <p style={{ color: "#a13b3b", fontSize: 13, marginTop: 16 }} data-testid="settings-err">{err}</p>}
        <div style={{ marginTop: 20 }}>
          <SpinnerButton loading={loading} type="button" onClick={save} dataTestId="settings-save-btn">Save</SpinnerButton>
        </div>
      </div>

      <h2 style={{ fontSize: 18, margin: "32px 0 16px 0" }}>Admin password</h2>
      <div className="scw-card" data-testid="settings-pw-card">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
          <div>
            <label className="scw-label">Current password</label>
            <input type="password" className="scw-input" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="settings-pw-current" />
          </div>
          <div>
            <label className="scw-label">{"New password (\u2265 8 chars)"}</label>
            <input type="password" className="scw-input" value={np} onChange={(e) => setNp(e.target.value)} data-testid="settings-pw-new" />
          </div>
        </div>
        {pwMsg && <p style={{ color: "#3a7d3a", fontSize: 13, marginTop: 16 }} data-testid="settings-pw-msg">{pwMsg}</p>}
        {pwErr && <p style={{ color: "#a13b3b", fontSize: 13, marginTop: 16 }} data-testid="settings-pw-err">{pwErr}</p>}
        <div style={{ marginTop: 20 }}>
          <SpinnerButton loading={pwLoading} type="button" onClick={changePassword} dataTestId="settings-pw-save-btn">Change password</SpinnerButton>
        </div>
      </div>
    </AdminLayout>
  );
}
